import { useState } from "react";

const DAY_LABELS = ["Lun", "Mar", "Mer", "Jeu", "Ven", "Sam", "Dim"];

function toDate(iso) {
  return new Date(typeof iso === "string" ? iso.slice(0, 10) : iso);
}

function addDays(date, n) {
  const d = new Date(date);
  d.setDate(d.getDate() + n);
  return d;
}

function sameDay(a, b) {
  return a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
}

function blocksForDay(day, ganttBlocks, startDate) {
  return ganttBlocks.filter(b => {
    const from = addDays(startDate, b.startDay - 1);
    const to   = addDays(startDate, b.startDay - 1 + b.duration - 1);
    return day >= from && day <= to;
  });
}

export default function CalendarView({ ganttBlocks = [], projectStartDate }) {
  const start = projectStartDate ? toDate(projectStartDate) : new Date();

  const [cursor, setCursor] = useState(() => {
    const d = new Date(start);
    d.setDate(1);
    return d;
  });

  const prevMonth = () => setCursor(c => { const d = new Date(c); d.setMonth(d.getMonth() - 1); return d; });
  const nextMonth = () => setCursor(c => { const d = new Date(c); d.setMonth(d.getMonth() + 1); return d; });

  const year  = cursor.getFullYear();
  const month = cursor.getMonth();

  const firstDay  = new Date(year, month, 1);
  // Monday-based: getDay() returns 0=Sun, offset to Mon=0
  const startOffset = (firstDay.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const totalCells  = Math.ceil((startOffset + daysInMonth) / 7) * 7;

  const monthLabel = cursor.toLocaleDateString("fr-FR", { month: "long", year: "numeric" });

  const cells = Array.from({ length: totalCells }, (_, i) => {
    const offset = i - startOffset;
    if (offset < 0 || offset >= daysInMonth) return null;
    const date = new Date(year, month, offset + 1);
    const blocks = blocksForDay(date, ganttBlocks, start);
    const isToday = sameDay(date, new Date());
    return { date, day: offset + 1, blocks, isToday };
  });

  return (
    <div style={{ background: "#fff", borderRadius: 12, overflow: "hidden", border: "1px solid #e5e7eb", maxWidth: 600, margin: "0 auto" }}>

      {/* Navigation */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 16px", borderBottom: "1px solid #f0f0f0" }}>
        <button onClick={prevMonth} style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 14, color: "#374151" }}>‹</button>
        <span style={{ fontWeight: 700, fontSize: 15, color: "#111827", textTransform: "capitalize" }}>{monthLabel}</span>
        <button onClick={nextMonth} style={{ background: "none", border: "1px solid #e5e7eb", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 14, color: "#374151" }}>›</button>
      </div>

      {/* Day labels */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", borderBottom: "1px solid #f0f0f0" }}>
        {DAY_LABELS.map(d => (
          <div key={d} style={{ padding: "6px 0", textAlign: "center", fontSize: 10, fontWeight: 700, color: "#9ca3af", letterSpacing: "0.05em" }}>{d}</div>
        ))}
      </div>

      {/* Grid */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)" }}>
        {cells.map((cell, i) => (
          <div key={i} style={{
            minHeight: 68,
            borderRight: (i + 1) % 7 !== 0 ? "1px solid #f3f4f6" : "none",
            borderBottom: i < totalCells - 7 ? "1px solid #f3f4f6" : "none",
            padding: "4px 3px",
            background: cell?.isToday ? "#fefce8" : "#fff",
          }}>
            {cell && (
              <>
                <div style={{
                  fontSize: 11,
                  fontWeight: cell.isToday ? 700 : 400,
                  color: cell.isToday ? "#d97706" : "#6b7280",
                  marginBottom: 3,
                  textAlign: "center",
                }}>
                  {cell.day}
                </div>
                {cell.blocks.map(b => (
                  <div key={b.id ?? b.label} style={{
                    background: b.color,
                    borderRadius: 3,
                    padding: "1px 4px",
                    marginBottom: 2,
                    fontSize: 9,
                    fontWeight: 600,
                    color: "#fff",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {b.label}
                  </div>
                ))}
              </>
            )}
          </div>
        ))}
      </div>

      {/* Légende */}
      {ganttBlocks.length > 0 && (
        <div style={{ padding: "10px 14px", borderTop: "1px solid #f0f0f0", display: "flex", flexWrap: "wrap", gap: 8 }}>
          {ganttBlocks.map(b => (
            <div key={b.id ?? b.label} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 10, color: "#374151" }}>
              <div style={{ width: 8, height: 8, borderRadius: 2, background: b.color, flexShrink: 0 }} />
              {b.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
